import { useState, useRef, useEffect } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { modalBackdrop, modalContent } from '@/utils/animations';

interface ImageGalleryProps {
  images: string[];
  alt?: string;
  className?: string;
}

export function ImageGallery({ images, alt = 'Product image', className }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  const currentImage = images[selectedIndex];

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLightboxOpen) {
        if (e.key === 'Escape') {
          setIsLightboxOpen(false);
        } else if (e.key === 'ArrowLeft') {
          handlePrev();
        } else if (e.key === 'ArrowRight') {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedIndex]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMousePosition({ x, y });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Main Image */}
      <div className="relative aspect-square bg-gray-100 dark:bg-dark-800 rounded-xl overflow-hidden group">
        <div
          ref={imageRef}
          className="relative w-full h-full cursor-zoom-in"
          onMouseEnter={() => setIsZoomed(true)}
          onMouseLeave={() => setIsZoomed(false)}
          onMouseMove={handleMouseMove}
          onClick={() => setIsLightboxOpen(true)}
        >
          <img
            src={currentImage}
            alt={`${alt} - Image ${selectedIndex + 1}`}
            className={cn(
              'w-full h-full object-contain transition-transform duration-200',
              isZoomed && 'scale-150'
            )}
            style={
              isZoomed
                ? {
                    transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                  }
                : undefined
            }
          />

          {/* Zoom Indicator */}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Hover to zoom
              </span>
            </div>
          </div>

          {/* Fullscreen Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsLightboxOpen(true);
            }}
            className={cn(
              'absolute bottom-4 right-4',
              'w-10 h-10 rounded-lg',
              'bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm',
              'flex items-center justify-center',
              'opacity-0 group-hover:opacity-100',
              'transition-all duration-200',
              'hover:scale-110 shadow-lg'
            )}
          >
            <ArrowsPointingOutIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          </button>
        </div>

        {/* Navigation Arrows (if multiple images) */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              className={cn(
                'absolute left-4 top-1/2 -translate-y-1/2',
                'w-10 h-10 rounded-full',
                'bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm',
                'flex items-center justify-center',
                'opacity-0 group-hover:opacity-100',
                'transition-all duration-200',
                'hover:scale-110 shadow-lg'
              )}
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className={cn(
                'absolute right-4 top-1/2 -translate-y-1/2',
                'w-10 h-10 rounded-full',
                'bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm',
                'flex items-center justify-center',
                'opacity-0 group-hover:opacity-100',
                'transition-all duration-200',
                'hover:scale-110 shadow-lg'
              )}
            >
              <ChevronRightIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            <span className="text-sm text-white font-medium">
              {selectedIndex + 1} / {images.length}
            </span>
          </div>
        )}
      </div>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={cn(
                'relative aspect-square rounded-lg overflow-hidden',
                'bg-gray-100 dark:bg-dark-800',
                'border-2 transition-all duration-200',
                selectedIndex === index
                  ? 'border-primary-500 ring-2 ring-primary-500/50'
                  : 'border-transparent hover:border-gray-300 dark:hover:border-dark-600'
              )}
            >
              <img
                src={image}
                alt={`${alt} - Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            variants={modalBackdrop}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            onClick={() => setIsLightboxOpen(false)}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className={cn(
                'absolute top-4 right-4 z-10',
                'w-12 h-12 rounded-full',
                'bg-white/10 backdrop-blur-sm',
                'flex items-center justify-center',
                'text-white hover:bg-white/20',
                'transition-colors duration-200'
              )}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            {/* Image Counter */}
            {images.length > 1 && (
              <div className="absolute top-4 left-4 z-10 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-white font-medium">
                  {selectedIndex + 1} / {images.length}
                </span>
              </div>
            )}

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrev();
                  }}
                  className={cn(
                    'absolute left-4 top-1/2 -translate-y-1/2',
                    'w-14 h-14 rounded-full',
                    'bg-white/10 backdrop-blur-sm',
                    'flex items-center justify-center',
                    'text-white hover:bg-white/20',
                    'transition-all duration-200 hover:scale-110'
                  )}
                >
                  <ChevronLeftIcon className="h-8 w-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                  className={cn(
                    'absolute right-4 top-1/2 -translate-y-1/2',
                    'w-14 h-14 rounded-full',
                    'bg-white/10 backdrop-blur-sm',
                    'flex items-center justify-center',
                    'text-white hover:bg-white/20',
                    'transition-all duration-200 hover:scale-110'
                  )}
                >
                  <ChevronRightIcon className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Main Image */}
            <motion.div
              variants={modalContent}
              initial="initial"
              animate="animate"
              exit="exit"
              className="relative max-w-7xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={currentImage}
                alt={`${alt} - Image ${selectedIndex + 1}`}
                className="max-w-full max-h-[calc(100vh-8rem)] object-contain"
              />
            </motion.div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-3xl">
                <div className="flex gap-2 overflow-x-auto px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIndex(index);
                      }}
                      className={cn(
                        'relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden',
                        'border-2 transition-all duration-200',
                        selectedIndex === index
                          ? 'border-white ring-2 ring-white/50 scale-110'
                          : 'border-transparent hover:border-white/50'
                      )}
                    >
                      <img
                        src={image}
                        alt={`${alt} - Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="absolute bottom-4 right-4 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <p className="text-sm text-white/80">
                Press <kbd className="px-1.5 py-0.5 bg-white/20 rounded">ESC</kbd> to close
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Skeleton Loader for ImageGallery
export function ImageGallerySkeleton() {
  return (
    <div className="space-y-4">
      {/* Main Image Skeleton */}
      <div className="relative aspect-square bg-gray-200 dark:bg-dark-800 rounded-xl animate-pulse" />

      {/* Thumbnails Skeleton */}
      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square bg-gray-200 dark:bg-dark-800 rounded-lg animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}
